import React from 'react';
import { Link } from 'react-router-dom';
import { socialLinks } from '../config/socialLinks';

const Footer = () => {
  return (
    <footer className="bg-white text-gray-900 py-20 pb-28 lg:pb-20 border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid md:grid-cols-5 gap-12 mb-12">
          {/* Company Info */}
          <div className="md:col-span-2 space-y-6">
            <div className="flex items-center space-x-3">
              <img
                src="/assets/images/Openbooq_blue-1773069231613.png"
                alt="OpenbooQ brand logo featuring modern 3D interlocking geometric design with circular cylindrical shapes in gradient blue, cyan and gray tones"
                className="h-14 w-auto object-contain"
              />
            </div>
            <p className="text-gray-600 text-sm leading-relaxed max-w-sm">
              Transparent roofing solutions with certified installers and transparent pricing.
              No markups, just results.
            </p>
            {/* Social Media Links */}
            <div className="flex space-x-4 pt-2">
              {socialLinks.map(({ platform, url, label, Icon }) => (
                <a
                  key={platform}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-primary hover:text-white transition-all duration-300 transform hover:scale-110"
                  aria-label={`Follow OpenbooQ on ${label}`}
                >
                  <Icon className="w-5 h-5" aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-gray-900 mb-6 text-base tracking-wide">Services</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/how-it-works" className="text-gray-600 hover:text-primary hover:translate-x-1 transition-all duration-200 inline-block">How It Works</Link></li>
              <li><Link to="/compare/quote" className="text-gray-600 hover:text-primary hover:translate-x-1 transition-all duration-200 inline-block">Compare a Quote</Link></li>
              <li><Link to="/shingles" className="text-gray-600 hover:text-primary hover:translate-x-1 transition-all duration-200 inline-block">Shingle Selection</Link></li>
              <li><Link to="/warranty" className="text-gray-600 hover:text-primary hover:translate-x-1 transition-all duration-200 inline-block">Warranty</Link></li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="font-bold text-gray-900 mb-6 text-base tracking-wide">Support</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/support" className="text-gray-600 hover:text-primary hover:translate-x-1 transition-all duration-200 inline-block">Help Center</Link></li>
              <li><Link to="/reviews" className="text-gray-600 hover:text-primary hover:translate-x-1 transition-all duration-200 inline-block">Customer Reviews</Link></li>
              <li><Link to="/for-contractors" className="text-gray-600 hover:text-primary hover:translate-x-1 transition-all duration-200 inline-block">For Contractors</Link></li>
              <li><Link to="/gallery" className="text-gray-600 hover:text-primary hover:translate-x-1 transition-all duration-200 inline-block">Project Gallery</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-bold text-gray-900 mb-6 text-base tracking-wide">Contact</h4>
            <ul className="space-y-4 text-sm">
              <li>
                <a href="tel:+1-937-910-8791" className="text-gray-600 hover:text-primary transition-colors duration-200 font-semibold flex items-center space-x-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span>(937) 910-8791</span>
                </a>
              </li>
              <li>
                <a href="mailto:connect@openbooq.com" className="text-gray-600 hover:text-primary transition-colors duration-200 flex items-center space-x-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>connect@openbooq.com</span>
                </a>
              </li>
              <li className="flex items-center space-x-2 text-gray-500 text-xs pt-1">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
                <span>Available 24/7</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-200 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-sm text-gray-500">
            © {new Date()?.getFullYear()} OpenbooQ. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 mt-4 md:mt-0">
            {/* Static HTML facts hub (served outside the SPA) — plain <a> so it
                does a full-page load to /ai/index.html, which non-JS AI crawlers
                can read and cite. Mirrors Modernize's "Official LLM Information". */}
            <a href="/ai" className="text-sm text-gray-500 hover:text-gray-900 transition-colors duration-200">
              Official LLM Information
            </a>
            <Link to="/privacy-policy" className="text-sm text-gray-500 hover:text-gray-900 transition-colors duration-200">
              Privacy Policy
            </Link>
            <Link to="/privacy-choices" className="text-sm text-gray-500 hover:text-gray-900 transition-colors duration-200">
              Your Privacy Choices
            </Link>
            <Link to="/terms-of-service" className="text-sm text-gray-500 hover:text-gray-900 transition-colors duration-200">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;